import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { Alert, Badge, Button, Dialog, Input, Radio, Stepper } from '@dreamtree-org/twreact-ui';
import { CheckCircle2, XCircle } from 'lucide-react';
import { KeyValue, Money, PageHeader, QueryState, SectionCard, StatusBadge } from '@/components';
import { useMutation, useQuery } from '@/lib/useQuery';
import { formatDate, formatINR } from '@/lib/format';
import { timelineSteps } from '../lib/timeline';
import SlotPicker, { dayLabel } from '../components/SlotPicker';

const when = (iso) => new Date(iso).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
const PART_STATUS = { in_stock: ['success', 'In stock'], ordered: ['warning', 'Ordered'], fitted: ['primary', 'Fitted'], unavailable: ['error', 'Unavailable'] };
const EST_STATUS = { pending: ['warning', 'Awaiting your approval'], approved: ['success', 'Approved'], declined: ['error', 'Declined'], superseded: ['default', 'Replaced by a newer version'] };

/** WEB-09 repair tracker: timeline, estimate versions with approve/decline, parts, QC, return scheduling. */
export default function RepairTracker() {
  const { id } = useParams();
  const q = useQuery('sfRepair', { id }, { select: (r) => r.data });
  const decide = useMutation('sfRepairDecide');
  const schedule = useMutation('sfRepairReturn');
  const pay = useMutation('sfRepairPay');
  const [payMethod, setPayMethod] = useState('upi');
  const [upiId, setUpiId] = useState('');
  const [confirm, setConfirm] = useState(null); // { version, decision }
  const [mode, setMode] = useState('delivery');
  const [slot, setSlot] = useState(null);
  // A drop-off repair can only be collected; doorstep returns use the agents' windows, collection the counter's.
  const returnMode = q.data && q.data.mode !== 'pickup' ? 'pickup' : mode;
  // Return windows for this own repair (a shopper action, so it still works while repairs are switched off).
  const slots = useQuery('sfRepairReturnSlots', { id, mode: returnMode }, { select: (r) => r.data, enabled: Boolean(q.data && q.data.can_schedule_return) });

  const doDecide = async () => {
    try {
      const r = await decide.run({ id, ...confirm });
      q.setData(r.data);
      setConfirm(null);
    } catch {
      /* shown in dialog */
    }
  };

  const doPay = async () => {
    try {
      const r = await pay.run({ id, method: payMethod, upi_id: payMethod === 'upi' ? upiId : undefined });
      q.setData(r.data);
    } catch {
      /* shown below */
    }
  };

  const doSchedule = async () => {
    try {
      const r = await schedule.run({ id, mode: returnMode, slot });
      q.setData(r.data);
    } catch {
      /* shown below */
    }
  };

  return (
    <div className="space-y-4">
      <QueryState query={q}>
        {(r) => {
          const { steps, currentStep } = timelineSteps(r.timeline, when, r.status);
          const pending = r.estimates.find((e) => e.status === 'pending');
          return (
            <>
              <PageHeader title={`Repair ${r.id}`} subtitle={`${r.device.model} · ${r.issue}`} back={{ label: 'My repairs', href: '/account/repairs' }} actions={<StatusBadge status={r.status} />} />
              {pending ? <Alert variant="warning" title="Your approval is needed">Estimate v{pending.version} is ready. Nothing will be repaired or charged until you approve.</Alert> : null}

              <div className="grid gap-5 lg:grid-cols-3">
                <div className="space-y-5">
                  <SectionCard title="Progress">
                    <Stepper steps={steps} currentStep={currentStep} orientation="vertical" />
                  </SectionCard>
                  <SectionCard title="Booking">
                    <KeyValue
                      items={[
                        { label: 'Booked', value: formatDate(r.created_at) },
                        { label: r.mode === 'pickup' ? 'Pickup' : 'Drop-off', value: r.slot ? `${dayLabel(r.slot.date)}, ${r.slot.label}` : null },
                        { label: 'Address', value: r.address },
                        { label: 'Diagnostic fee', value: <><Money paise={r.diagnostic_fee_paise} /> (waived on approval)</> },
                      ]}
                    />
                  </SectionCard>
                </div>

                <div className="space-y-5 lg:col-span-2">
                  <SectionCard title="Estimates">
                    {!r.estimates.length ? <p className="text-sm opacity-70">The technician will share a written estimate after diagnosis.</p> : null}
                    <div className="space-y-3">
                      {[...r.estimates].reverse().map((e) => {
                        const [variant, label] = EST_STATUS[e.status] || ['default', e.status];
                        return (
                          <div key={e.version} className={`space-y-2 rounded-lg border p-3 ${e.status === 'pending' ? 'border-warning-500' : 'border-[var(--app-border)]'} ${e.status === 'superseded' ? 'opacity-70' : ''}`}>
                            <div className="flex flex-wrap items-center justify-between gap-2">
                              <span className="font-semibold">Version {e.version} · {formatDate(e.created_at)}</span>
                              <Badge size="xs" variant={variant}>{label}</Badge>
                            </div>
                            {e.note ? <p className="text-sm opacity-80">{e.note}</p> : null}
                            <dl className="space-y-1 text-sm">
                              {e.lines.map((l) => (
                                <div key={l.label} className="flex justify-between gap-3"><dt>{l.label} <span className="text-xs opacity-60">({l.kind})</span></dt><dd><Money paise={l.amount_paise} /></dd></div>
                              ))}
                              <div className="flex justify-between gap-3 border-t border-[var(--app-border)] pt-1 font-semibold"><dt>Total incl. GST</dt><dd><Money paise={e.total_paise} /></dd></div>
                            </dl>
                            {e.status === 'pending' ? (
                              <div className="flex flex-wrap gap-2 pt-1">
                                <Button onClick={() => setConfirm({ version: e.version, decision: 'approve' })}>Approve&nbsp;<Money paise={e.total_paise} /></Button>
                                <Button variant="outline" onClick={() => setConfirm({ version: e.version, decision: 'decline' })}>Decline</Button>
                              </div>
                            ) : null}
                          </div>
                        );
                      })}
                    </div>
                  </SectionCard>

                  <div className="grid gap-5 md:grid-cols-2">
                    <SectionCard title="Parts">
                      {!r.parts.length ? <p className="text-sm opacity-70">No parts needed yet.</p> : (
                        <ul className="space-y-2 text-sm">
                          {r.parts.map((p) => {
                            const [variant, label] = PART_STATUS[p.status] || ['default', p.status];
                            return (
                              <li key={p.name} className="flex flex-wrap items-center justify-between gap-2">
                                <span>{p.name}</span>
                                <span className="flex items-center gap-2"><Badge size="xs" variant={variant}>{label}</Badge>{p.eta ? <span className="text-xs opacity-70">ETA {formatDate(p.eta)}</span> : null}</span>
                              </li>
                            );
                          })}
                        </ul>
                      )}
                    </SectionCard>
                    <SectionCard title="Quality check">
                      {!r.qc.length ? <p className="text-sm opacity-70">Runs after the repair: stress test, ports, keyboard and your reported issue.</p> : (
                        <ul className="space-y-2 text-sm">
                          {r.qc.map((c) => (
                            <li key={c.name} className="flex items-start gap-2">
                              {c.result === 'pass' ? <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-success-600" aria-label="Pass" /> : <XCircle className="mt-0.5 h-4 w-4 shrink-0 text-error-600" aria-label="Fail" />}
                              {c.name}
                            </li>
                          ))}
                        </ul>
                      )}
                    </SectionCard>
                  </div>

                  {r.invoice ? (
                    <SectionCard title="Invoice" actions={<Badge variant={r.invoice.status === 'paid' ? 'success' : 'warning'} size="sm">{r.invoice.status === 'paid' ? 'Paid' : 'Payment due'}</Badge>}>
                      <KeyValue
                        items={[
                          { label: 'Invoice', value: r.invoice.number },
                          { label: 'Total incl. GST', value: <Money paise={r.invoice.total_paise} /> },
                          r.invoice.paid_at ? { label: 'Paid', value: `${formatDate(r.invoice.paid_at)} · ${r.invoice.method === 'online' ? 'online' : `at the store (${r.invoice.method})`}` } : null,
                        ]}
                      />
                      {r.invoice.can_pay ? (
                        <div className="space-y-3">
                          <div className="flex flex-wrap gap-4">
                            {[['upi', 'UPI'], ['card', 'Card'], ['netbanking', 'Net banking']].map(([v, l]) => (
                              <Radio key={v} name="repair-pay" value={v} checked={payMethod === v} onChange={() => setPayMethod(v)} label={l} radioColor="var(--color-primary-600)" />
                            ))}
                          </div>
                          {payMethod === 'upi' ? <Input label="UPI ID" value={upiId} onChange={(e) => setUpiId(e.target.value)} placeholder="name@bank" /> : null}
                          {pay.error ? <Alert variant="error">{pay.error}</Alert> : null}
                          <Button loading={pay.loading} disabled={payMethod === 'upi' && !/^[\w.-]+@[\w.-]+$/.test(upiId)} onClick={doPay}>
                            {`Pay ${formatINR(r.invoice.total_paise)} now`}
                          </Button>
                          <p className="text-xs opacity-70">You can also pay at the store when you collect the device.</p>
                        </div>
                      ) : null}
                    </SectionCard>
                  ) : null}

                  <SectionCard title="Return of your device">
                    {r.return_schedule ? (
                      <Alert variant="success" title="Return scheduled">
                        {r.return_schedule.mode === 'delivery' ? 'Doorstep delivery' : 'Store pickup'} on {dayLabel(r.return_schedule.slot.date)}, {r.return_schedule.slot.label}.
                      </Alert>
                    ) : r.can_schedule_return ? (
                      <div className="space-y-3">
                        <p className="text-sm">{r.status === 'declined' ? 'You declined the estimate. We will return the device unrepaired; the diagnostic fee applies.' : 'Your device passed quality check. Choose how to get it back.'}</p>
                        <div className="flex flex-wrap gap-4">
                          {r.mode === 'pickup' ? <Radio name="return-mode" value="delivery" checked={mode === 'delivery'} onChange={() => { setMode('delivery'); setSlot(null); }} label="Deliver to my address" radioColor="var(--color-primary-600)" /> : null}
                          <Radio name="return-mode" value="pickup" checked={returnMode === 'pickup'} onChange={() => { setMode('pickup'); setSlot(null); }} label="I'll collect from the store" radioColor="var(--color-primary-600)" />
                        </div>
                        <SlotPicker days={slots.data} value={slot} onChange={setSlot} />
                        {schedule.error ? <Alert variant="error">{schedule.error}</Alert> : null}
                        <Button disabled={!slot} loading={schedule.loading} onClick={doSchedule}>Schedule return</Button>
                      </div>
                    ) : (
                      <p className="text-sm opacity-70">You can schedule the return once the repair passes quality check.</p>
                    )}
                  </SectionCard>
                </div>
              </div>

              <Dialog
                isOpen={!!confirm}
                onClose={() => setConfirm(null)}
                title={confirm && confirm.decision === 'approve' ? 'Approve this estimate?' : 'Decline this estimate?'}
                footer={
                  <div className="flex flex-wrap justify-end gap-2">
                    <Button variant="ghost" onClick={() => setConfirm(null)}>Back</Button>
                    <Button variant={confirm && confirm.decision === 'decline' ? 'destructive' : 'primary'} loading={decide.loading} onClick={doDecide}>
                      {confirm && confirm.decision === 'approve' ? 'Approve' : 'Decline'}
                    </Button>
                  </div>
                }
              >
                <div className="space-y-2 text-sm">
                  {confirm && confirm.decision === 'approve' ? (
                    <p>The technician starts the repair. You pay the estimate total on return; the diagnostic fee is waived.</p>
                  ) : (
                    <p>We will return the device unrepaired. The diagnostic fee of <Money paise={r.diagnostic_fee_paise} /> applies.</p>
                  )}
                  {decide.error ? <Alert variant="error">{decide.error}</Alert> : null}
                </div>
              </Dialog>
            </>
          );
        }}
      </QueryState>
    </div>
  );
}
